import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import useCheckAccountSetup from "~/hooks/useCheckAccountSetup";
import { getLocalStorageItem } from "~/lib/localStorage";

import useCurrentUser from "./user/useCurrentUser";

/**
 * @function useHandleOnLoadTriggers
 * @description Custom hook for managing triggering specific actions on load
 */
const useHandleOnLoadTriggers = () => {
  const flowCompleted = useRef(false);
  const { isLoading: isUserLoading, data: userData } = useCurrentUser();

  const { data, isLoading: isAccountSetupLoading } = useCheckAccountSetup();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    const handleTriggers = () => {
      if (!isUserLoading && !isAccountSetupLoading && !flowCompleted.current) {
        // Page has loaded, and user data is available
        // Stop the flow from running again on subsequent renders.
        flowCompleted.current = true;

        // Is the user a tenant or landlord?
        const role = userData?.internalRole;

        if (role === "landlord") {
          const documentPrePaywall = getLocalStorageItem("documentPrePaywall");

          if (documentPrePaywall) {
            localStorage.removeItem("documentPrePaywall");

            // A user bailed from paying on the form flow. Redirect them to the document.
            navigate(`/landlord/documents/${documentPrePaywall}`);
            return;
          }

          // If we just logged in, we want to check if the user has completed the account setup.
          if (pathname === "/landlord") {
            const accountSetupSteps = {
              userVerifiedEmail: data?.userVerifiedEmail,
              businessInfo: data?.businessInfo,
              payments: data?.payments,
              leases: data?.leases,
              tenants: data?.tenants,
              properties: data?.properties,
              rent: data?.rent,
            };

            const accountSetupStepsTotal =
              Object.keys(accountSetupSteps || {}).length || 0;

            const accountSetupStepsComplete =
              Object.values(accountSetupSteps || {}).filter(
                (step) => step === "complete"
              ).length || 0;

            // If the user has not completed the account setup, redirect them to the account setup page.
            if (
              accountSetupStepsTotal > 0 &&
              accountSetupStepsComplete < accountSetupStepsTotal &&
              !data?.setupSkipped
            ) {
              navigate("/landlord/account-setup");
            }
          }
        }

        // Has the user completed the onboarding process?
        // Add any additional checks here
      }
    };

    handleTriggers();
  }, [
    isUserLoading,
    userData,
    data,
    isAccountSetupLoading,
    navigate,
    pathname,
  ]);
};

export default useHandleOnLoadTriggers;
